// Adapted from jdb-online-consult-entry-v2 for the site's Bearer-token login.
import { requestUrl } from '@/config/app';

const unavailable = '在线咨询暂时无法打开，请稍后重试。';
const currentToken = () => window.localStorage.getItem('token') || '';

async function request(action, token, signal) {
  const response = await window.fetch(`${requestUrl}/api/v1/customer-service/${action}`, {
    method: action === 'entry' ? 'POST' : 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    signal,
    cache: 'no-store',
  });
  const payload = await response.json().catch(() => null);
  if (response.status === 401) throw new Error('请先登录');
  if (!response.ok || !payload) {
    throw new Error(response.status === 400 && payload && payload.message ? payload.message : unavailable);
  }
  return payload;
}

function requestNotificationPermission() {
  if (!('Notification' in window) || window.Notification.permission !== 'default') return;
  try {
    const pending = window.Notification.requestPermission();
    if (pending && pending.catch) pending.catch(() => {});
  } catch (_) { /* Notifications are optional, including on unsupported mobile browsers. */ }
}

// Obtain a ticket for the local chat component; never navigate the page.
export async function openOnlineConsult() {
  const token = currentToken();
  if (!token) throw new Error('请先登录后再使用在线咨询');
  requestNotificationPermission();
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 12000);
  try {
    const payload = await request('entry', token, controller.signal);
    if (currentToken() !== token) throw new Error('登录状态已变化，请重新点击在线咨询');
    const url = new URL(payload.service_base);
    if (url.protocol !== 'https:' || url.username || url.password || url.pathname !== '/' || url.search || url.hash ||
      typeof payload.ticket !== 'string' || !payload.ticket.trim() || payload.client !== 'yinlong' ||
      !Number.isInteger(payload.expires_in) || payload.expires_in <= 0 || payload.expires_in > 300) throw new Error(unavailable);
    return { origin: url.origin, ticket: payload.ticket, client: payload.client, expires_in: payload.expires_in };
  } catch (error) {
    throw new Error(error.name === 'AbortError' ? unavailable : error.message || unavailable);
  } finally {
    window.clearTimeout(timeout);
  }
}

export function startUnreadMonitor(onChange, onNotificationClick) {
  const token = currentToken();
  if (!token) return { stop() {} };
  let disposed = false;
  let controller = null;
  let initialized = false;
  let previousCount = 0;
  let notification = null;

  function notify() {
    if (!('Notification' in window) || window.Notification.permission !== 'granted') return;
    if (!document.hidden && document.hasFocus()) return;
    try {
      if (notification) notification.close();
      notification = new window.Notification('客服回复提醒', {
        body: '您有新的在线咨询回复，点击在线咨询查看。',
        tag: 'yinlong-online-consult',
      });
      notification.onclick = () => {
        window.focus();
        notification.close();
        if (!disposed && currentToken() === token) onNotificationClick();
      };
    } catch (_) { /* Red dots work even when the browser cannot show notifications. */ }
  }

  async function refresh() {
    if (disposed || controller || document.hidden || currentToken() !== token) return;
    controller = new AbortController();
    const timeout = window.setTimeout(() => controller && controller.abort(), 12000);
    try {
      const payload = await request('unread', token, controller.signal);
      if (disposed || currentToken() !== token) return;
      const count = payload.unread_count;
      if (!Number.isInteger(count) || count < 0) return;
      onChange(count);
      if (initialized && previousCount === 0 && count > 0) notify();
      previousCount = count;
      initialized = true;
    } catch (error) {
      // Preserve the last dot on transient failure; never repeatedly show errors.
      if (!disposed && currentToken() === token && error.message === '请先登录') {
        onChange(0);
        stop();
      }
    } finally {
      window.clearTimeout(timeout);
      controller = null;
    }
  }

  function onVisibilityChange() {
    if (document.hidden) {
      if (controller) controller.abort();
    } else refresh();
  }
  function onStorage(event) {
    if (event.key === 'token' || event.key === null) {
      if (currentToken() !== token) { onChange(0); stop(); }
    }
  }
  const timer = window.setInterval(refresh, 60000);
  function stop() {
    disposed = true;
    window.clearInterval(timer);
    if (controller) controller.abort();
    if (notification) notification.close();
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('storage', onStorage);
  }
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('storage', onStorage);
  refresh();
  return { stop };
}
